/**
 * 全局阈值常量 —— **单一常量源**（架构 §5.5：「阈值集中为单一常量文件」）
 *
 * 依据：规格 §3.7.1（验收数值，`DR-006`）· §5（预算）· §12（快照，`DR-012`）·
 * §16（协作）· §20（数据主权）· §11（AI 先验，`DR-014`）· 架构 §5.5。
 *
 * 🔴 **两条纪律**：
 * 1. **比率先行、标度统一** —— 一切"百分比 / 占比"类阈值以 **万分之一整数**
 *    （后缀 `E4`，`10000` = 100%）表达，与 `money.ts` 的 `ratioE4()` **同标度**
 *    ⇒ 判定时是**整数比较**，内核与消费端都**不需要再做除法**（守 §22.1 禁浮点）。
 * 2. **禁止散落字面量** —— 调用方一律引用本文件；`P0清单` 与各域 PRD 里的数字
 *    若与本文件不符，**以本文件为准**（本文件逐条附来源）。
 */

// ─────────────────────── 1. 列表 / 分页（规格 §3.7.1） ───────────────────────

export const ListConfig = {
  /** 分页页大小（条） */
  PageSize: 50,
  /**
   * 单页**上限裁剪**（条）—— 架构 §6.4「统一分页中间件…与上限裁剪」。
   *
   * 🔴 超上限**报 400**、**不静默截断**：静默截断会让客户端以为拿到了自己请求的页长
   * （翻页错位的经典成因）。`200` 与虚拟滚动阈值同量级 —— 一屏最多也就一页。
   */
  MaxPageSize: 200,
  /** 虚拟滚动启用阈值：单次待渲染 **> 200 条** 才启用 */
  VirtualScrollThreshold: 200,
  /** 流水列表默认时间窗 = **近 3 个月** */
  DefaultWindowMonths: 3,
  /** 单笔交易标签数上限（架构 §4.2 C 组 `transaction_tags`） */
  MaxTagsPerTransaction: 5,
} as const;

// ─────────────────────── 2. 性能验收（规格 §3.7.1 · `DR-006`） ───────────────────────

/**
 * 🔴 **验收环境铁律**：必须在与生产同规格（**2 核 2G**）的机器上、
 * 用**合成数据 15,000 条**实测，逐项出数留档；
 * 禁止用开发机与当前小数据量（920 条）测试后判定达标。
 */
export const PerfBudget = {
  /** 流水列表首屏（默认近 3 月）P95 */
  FirstScreenP95Ms: 800,
  /** 翻页 / 加载更多（50 条）P95 */
  PageP95Ms: 400,
  /** 筛选响应（分类 / 账户 / 类型）P95 */
  FilterP95Ms: 500,
  /** 关键词检索 P95 */
  SearchP95Ms: 600,
  /** 服务端常驻内存上限（MB）—— `P域PRD` 的框架退出条件即以此实测触发 */
  ServerMemLimitMb: 200,
  /** 验收压力数据量（合成 `transactions` 条数） */
  StressTxnCount: 15000,
} as const;

// ─────────────────────── 3. 预算（规格 §5 · 架构 §5.5 · `DR-011`） ───────────────────────

/**
 * 预算档位阈值 —— 单位 **`E4`（万分之一）**，`10000` = 100%。
 *
 * 🔴 判定规则分两类（规格 §5「预警规则」）：
 * - **月度**：花费进度 ≥ `WarnE4` 黄 / ≥ `OverE4` 红（**绝对阈值**）
 * - **季 / 年 / 事件**：按「时间进度 vs 花费进度」**偏离度**判档（**相对阈值**）
 *   —— 偏离 = 花费进度 − 时间进度；≥ `DeviationWarnE4` 黄 / ≥ `DeviationOverE4` 红。
 */
export const BudgetThreshold = {
  /** 月度：≥80% 黄 */
  WarnE4: 8000,
  /** 月度：≥100% 红 */
  OverE4: 10000,
  /** 季/年/事件：偏离 +10% 黄 */
  DeviationWarnE4: 1000,
  /** 季/年/事件：偏离 +30% 红 */
  DeviationOverE4: 3000,
  /** 新建建议三档之一：**收入 30%**（另两档 = 上月支出 / 近 3 月均值，非比率） */
  IncomeSuggestE4: 3000,
  /** 线性外推预计：**时间进度 < 10% 时不显示**（样本太少，外推无意义） */
  ExtrapolateMinTimeProgressE4: 1000,
} as const;

/**
 * 🔴 总预算占位键（规格 §5）：**总预算花费 = 窗口内全部支出合计**，
 * **不得按此键查聚合**（否则得 0）。
 */
export const BudgetTotalKey = '__total__' as const;

// ─────────────────── 3b. 必付日历（F 域 F2 · 规格 §12 J3） ───────────────────

/** F2「未来必付日历」的窗口口径（与 H 域、J3 **共用同一份聚合**，禁各算） */
export const UpcomingPolicy = {
  /** 默认窗口（天）—— F2 一屏排开的范围 */
  DefaultWindowDays: 30,
} as const;

// ─────────────────── 3c. 调度（架构 §8.1 / §8.2 · `AC-P2.1`） ───────────────────

/**
 * 调度策略常量。
 *
 * 🔴 **`MaxCatchUpDays` 的存在理由**：水位补跑本身是"停机跨 tick 不漏期"的解法，
 * 但**无上限的补跑**会在长时间停机后一次性跑爆（既耗时又可能撞配额/外部 API 限流）。
 * ⇒ 超出上限时**只补最近 N 天**，并把**被截断的天数回报给调用方**去记日志
 * （**不静默丢**——静默丢弃正是"少了一天没人知道"的经典成因）。
 */
export const SchedulerPolicy = {
  /** 周期生成 tick 间隔（ms）—— 架构 §8.1「进程内 60s 轮询 + 启动补跑」 */
  TickIntervalMs: 60000,
  /** 单次水位补跑的天数上限 —— 超出只补最近 N 天并回报截断量 */
  MaxCatchUpDays: 31,
  /** 水位新鲜度阈值（小时）—— 超过即视为调度停摆（`/healthz` 据此降级） */
  WatermarkStaleHours: 26,
} as const;

// ─────────────────── 3d. 周期规则（F 域 F1 · 规格 §18 · `DR-010`） ───────────────────

/**
 * 周期规则策略常量（规格 §18 · F 域 PRD §4.1 / §4.2）。
 *
 * ⚠️ **除 `MaxCatchUpPeriods` 外均为产品口径**（`NameMaxLength` 除外 —— 见其注释）。
 */
export const SchedulePolicy = {
  /**
   * 单条规则**单轮**最多推进的期次（安全阀）。
   *
   * 🔴 **它不是产品口径**，而是防"脏数据炸库"：`nextRunAt` 若被写成一个远古日期
   * （手改库 / 迁移异常），`daily` 规则会试图补齐数万期。
   * 取 **400** 的理由：调度基座自身的补跑上限是 `SchedulerPolicy.MaxCatchUpDays`（31 天）
   * ⇒ 正常路径下 `daily` 最多 31 期；400 留出十倍以上余量，
   * 但远小于"远古日期"会产生的量级。**超出即停**（并在日志里记截断量，不静默）。
   */
  MaxCatchUpPeriods: 400,
  /**
   * 规则名长度上限（字符）。
   * ⚠️ **非产品口径**（规格与 F 域 PRD 均未规定）；量级参照账户名（30）。
   */
  NameMaxLength: 30,
} as const;

// ─────────────────────── 4. 协作（规格 §16 · L 域） ───────────────────────

export const Collaboration = {
  /** 账本成员上限（**服务端硬校验**，架构 §4.2 A 组） */
  MemberLimit: 4,
  /** 邀请码长度（位） */
  InviteCodeLength: 8,
  /** 邀请码有效期（天） */
  InviteTtlDays: 7,
} as const;

/**
 * 邀请码字符集 —— 🔴 **排除易混淆字符 `I` / `O` / `0` / `1`**（规格 §16）。
 *
 * 基数恰为 **32**（26 字母 − `I`/`O` = 24，10 数字 − `0`/`1` = 8）——
 * 一个字符正好占 5 bit，生成器可直接按 5 位分组取字符（无取模偏置）。
 */
export const InviteCodeAlphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

// ─────────────────── 4b. 认证与租户（架构 §6.2 / §11） ───────────────────

/**
 * 认证与租户策略。
 *
 * 🔴 **`LoginMaxFailures` / `LoginLockMs` 的取值直接来自 v1 事故**（架构 §3.2 防坑①）：
 * v1 未配 `trust proxy` + **按全局计数**限流 ⇒ 「任意人失败 5 次 = **全站**登录 401 十五分钟」。
 * ⇒ v2 的限流**只按 `req.ips[0]`（单 IP）计数**，锁的是那个 IP，不是整个站点。
 * 参数沿用 5 / 15 分钟，**但计量维度必须换掉** —— 换维度才是修 bug，换参数只是掩盖。
 */
export const AuthPolicy = {
  /** 单 IP 登录失败上限（次）—— 达到即锁 */
  LoginMaxFailures: 5,
  /** 单 IP 登录锁定时长（ms）= 15 分钟 */
  LoginLockMs: 15 * 60 * 1000,
  /** 密码哈希代价（bcrypt rounds）—— bcryptjs 默认 10，显式写死防被改 */
  PasswordHashRounds: 10,
  /**
   * 🔴 账本上下文的请求头名 —— **唯一来源**（架构 §6.2「`X-Ledger-Id` 是账本上下文的唯一来源」）。
   *
   * 服务端守卫按此名读取；**Web 端与小程序端也从本常量取**（而不是各自写字符串）
   * —— 少一处手抄，就少一次「改了名字一半代码失效」。
   * Express 的 `req.headers` 键为**全小写**，故此处即小写形态。
   */
  LedgerHeader: 'x-ledger-id',
  /** 幂等键请求头名（架构 §6.5） */
  IdempotencyHeader: 'idempotency-key',
} as const;

// ─────────────────────── 5. 净资产快照（规格 §12 · `DR-012`） ───────────────────────

export const SnapshotPolicy = {
  /** 回填上限 = **36 个月（3 年）** */
  BackfillMaxMonths: 36,
  /**
   * 曲线最少点数：**少于 2 个点显示空态「数据积累中」**
   * （1 个点画不出线；且历史缺口是重资产/持仓/保单等**时点估值**，
   * 「历史值不存在」⇒ 禁止用部分口径补画 —— 规格 §12「曲线只用真实快照」）
   */
  MinPointsForCurve: 2,
  /**
   * 「净资产变化来源」分解的**穷举**五来源（规格 §12 · `DR-012`）。
   * ⚠️ 残差归「其他调整」；🔴 **禁止出现「系统调整」类不可解释项**。
   */
  DeltaSources: [
    'operatingBalance', // 收支结余
    'investmentGain', // 投资浮盈
    'autoAccrual', // 待缴自动生成
    'fxMovement', // 汇率变动
    'manualAdjustment', // 手动调整（含 `DR-016` 追加的「单位缴纳」= 公积金企业缴费）
  ],
} as const;

// ─────────────────────── 6b. 汇率（架构 §4 行 587 · `DR-004` §2.5 · `汇率域口径对齐单`） ───────────────────────

export const RatePolicy = {
  /**
   * 汇率上限（**万分之一整数**）—— 即 `1 单位 base` 最多兑 `100000 单位 quote`。
   *
   * ⚠️ **非产品口径**（规格未规定），是**防御性上限**：挡住"手滑多打几个 0"
   * 造成的天文数字折算（与 `AccountPolicy.InitialBalanceAbsLimitCents` 同类）。
   * 规格若给出正式值，以正式值为准 —— 改此一处即可。
   */
  MaxRateE4: 1_000_000_000,
} as const;

// ─────────────────────── 6. 数据主权（规格 §20 · N 域） ───────────────────────

export const DataSovereignty = {
  /** 注销冷静期（天）：**= 冻结态**，可随时撤销，剩余天数可见 */
  DeletionCooldownDays: 7,
  /** 软删保留期（天）：`transactions.deletedAt`（架构 §4.1） */
  SoftDeleteRetentionDays: 30,
} as const;

// ─────────────────────── 7. AI 策略（规格 §11 · `DR-014`） ───────────────────────

export const AiPolicy = {
  /** 智能分类「历史先验」窗口 = **近 90 天** */
  PriorWindowDays: 90,
  /** 先验生效门槛：同商户笔数 ≥ 3 */
  PriorMinCount: 3,
  /** 先验生效门槛：同商户占比 ≥ 80%（`E4` 标度） */
  PriorMinRatioE4: 8000,
  /** 「本月三件事」**取前 3、不凑数**（不足 3 条就显示几条） */
  MonthlyHighlights: 3,
} as const;

// ─────────────────────── 8. 财务健康（规格 §12 · `DR-013`） ───────────────────────

export const HealthPolicy = {
  /** 「近 3 个自然月月均」的分母窗口（≠ 窗口内日均，两套概念禁同名混用） */
  WindowMonths: 3,
} as const;

// ─────────────────────── 8b. 报表（K 域 · 规格 §12 · `DR-013` / `DR-015`） ───────────────────────

/**
 * K 域报表的**阈值与窗口**单一源（`AC-K1.11`：v1 把 30 / 400 / 24 硬编码在页面里 ⇒ 本批提为常量）。
 *
 * ⚠️ **两类常量，语义不同**：
 * - `DefaultTrendMonths` / `MaxTrendMonths` / `DailyAverage*` —— **取数窗口**的下限/上限（纯工程约束）；
 * - `EmergencyMonthsTarget` 及以下四条 —— 🔴 **「每层一个动作」的判定阈值**，是**产品口径**，
 *   本批**首次定义**（K 域 PRD §4.5.4 只给了示例文案，未给数值）：示例「应急金只够 2.3 个月
 *   （低于常见 3 个月线）→ **每月多存 ¥1,500 可到 3 个月**」⇒ 反推 `EmergencyMonthsTarget = 3`
 *   与 `AdviceMonthsToTarget = 6`；示例「**两处各压 10%**，储蓄率可到 **15%** 附近」⇒ 反推
 *   `ExpenseCutRatioE4 = 1000` 与 `SavingsRateTargetE4 = 1500`。**改动这些数 = 改建议行为**。
 */
export const ReportPolicy = {
  /** 趋势缺省月数（K 域 §4.2：12 月档） */
  DefaultTrendMonths: 12,
  /** 趋势月数上限（= 3 年档，与 J2 曲线的 `36m` 对齐） */
  MaxTrendMonths: 36,
  /**
   * 财务健康的**年度口径窗口**（月）—— `annualIncome` / `annualPremium` 的分母窗口。
   *
   * ⚠️ 「年度」在本项目取**近 12 个完整自然月**（不含本月 —— 本月尚未过完，
   * 拿它做分母会系统性偏小），与 `HealthPolicy.WindowMonths = 3` 同一手法。
   */
  HistoryMonths: 12,
  /** 「窗口内日均」的默认天数（v1 硬编码 30） */
  DailyAverageDefaultDays: 30,
  /** 「窗口内日均」的天数上限（v1 硬编码 400 —— 超过则改用月度均值，防长窗口失真） */
  DailyAverageMaxDays: 400,
  /** σ 离群阈值倍数（`E4`；`20000` = `2.0σ`） */
  OutlierThresholdE4: 20000,
  /** 🔴 应急月数的目标线（月）—— 风险线动作的判据 */
  EmergencyMonthsTarget: 3,
  /** 🔴 达成目标线所给的缓冲期（月）—— 决定"每月多存多少" */
  AdviceMonthsToTarget: 6,
  /** 🔴 效率线动作里"压支出"的比例（`E4`；`1000` = 10%） */
  ExpenseCutRatioE4: 1000,
  /** 🔴 储蓄率目标（`E4`；`1500` = 15%）—— 低于它才给效率线动作 */
  SavingsRateTargetE4: 1500,
  /** 🔴 保费比上限（`E4`；`1000` = 10%）—— 高于它才给结构线动作 */
  PremiumRatioHighE4: 1000,
} as const;

// ─────────────────── 3g. 提醒（H 域 · 架构 §8.1 · H 域 PRD §5.1.2 / §5.3） ───────────────────

/**
 * 提醒策略常量（H 域 PRD **§5.1.2** 提前量与重复策略 · **§5.3.3** 免打扰 · 架构 **§8.1** 生成时刻）。
 *
 * 🔴 **这些数是本批（`56`）首次从「原型文案 + PRD 表格」落为代码常量** —— 此前全仓零常量
 * （提前量只出现在 `09-提醒中心_原型.html` 的说明文字里）。**改动这些数 = 改推送行为**，
 * `AC-H1.1` / `AC-H1.2` / `AC-H3.4` 直接判它。
 */
export const ReminderPolicy = {
  /** 🔴 还款日提醒**提前量**（天）—— 提前 N 天 + 到期当天**各 1 次**（`AC-H1.1`） */
  RepaymentLeadDays: 5,
  /** 🔴 账单日提醒**延后量**（天）—— 出账后**次日**推 1 次（`AC-H1.2`） */
  BillingNotifyAfterDays: 1,
  /** 🔴 逾期后提醒**间隔**（天）—— 每日 1 次直至处理（`AC-H1.14`） */
  OverdueRepeatDays: 1,
  /** 免打扰时段默认起点（**账本时区下的本地时刻** `HH:mm`）—— `AC-H3.4` */
  DefaultDndStart: '22:00',
  /** 免打扰时段默认终点（跨零点 —— 判定必须处理环绕）—— `AC-H3.4` */
  DefaultDndEnd: '07:30',
  /** 提醒集中生成时刻（本地 `HH:mm`）—— 架构 §8.1「每日 **08:30**」 */
  GenerateAt: '08:30',
  /** 周检视生成时刻（**周日**本地 `HH:mm`）—— `AC-H1.7` */
  WeeklyReviewAt: '09:00',
  /**
   * 「更早」档（已读/已完成）单次返回的**上限**。
   * 理由：提醒是**逐日累积**的，长跑后历史无界 ⇒ 列表必须有界（否则首屏越用越慢，
   * 与 `DR-006` 的性能纪律冲突）。⚠️ **截断必须上报**（`earlierTruncated`），不静默。
   */
  EarlierLimit: 50,
} as const;

// ───────────────────────── 8.5 设置（N 域 · 用户级偏好） ─────────────────────────

/**
 * 用户偏好策略（N 域 §5.2 / §5.3 · `AC-N2.18`）。
 *
 * 🔴 **`Default` 必须与"没有偏好行"时的下发值一致**（`setting.service.ts` 的兜底）——
 * 不一致就是"新用户和改过别的偏好的老用户默认窗口不同"这类难查的错。
 */
export const TxnWindowPolicy = {
  /**
   * 流水默认时间窗的**默认档** = **近 3 个月**。
   * 依据：N 域 PRD §5.2 ⑤「默认近 3 个月」+ `AC-N2.18` + 原型 `08-家庭与设置_原型.html` 的 select 首项；
   * 🔴 与 C 域 `ListConfig.DefaultWindowMonths = 3`（服务端默认窗口）**同值** —— 两处必须一起改。
   */
  Default: '3m',
} as const;

// ───────────────────────── 9. 流水（C 域 · 规格 §3） ─────────────────────────

export const TransactionPolicy = {
  /** 🔴 批量操作上限 **200 笔**（C 域 §5.1.5 / `AC-C1.22`：对齐虚拟滚动阈值） */
  MaxBatchSize: 200,
  /**
   * 备注**防御性**长度上限（字符）。
   *
   * ⚠️ **这不是产品口径** —— 规格与 C 域 PRD **均未规定**备注长度。此处设上限只为避免
   * 「无界字符串落库 + 回显」这一类廉价风险；`AC-C1.16`/规格若将来给出正式值，**以正式值为准并改此处**。
   * 首次登记见 `docs/changelog/2026-09-29-journal-txn-core.md`。
   */
  NoteMaxLength: 200,
  /**
   * 标签名**防御性**长度上限（字符）。
   * ⚠️ 同 {@link TransactionPolicy.NoteMaxLength}：非产品口径，未在规格定义；量级参照账本名（30）。
   */
  TagNameMaxLength: 20,
  /**
   * 工资条（多腿单交易）的**子项上限** —— `DR-016` §3.8④。
   * 理由：税前 / 补贴（2）+ 社保（养老 · 年金 · 医保 · 失业）+ 公积金（2）+ 个税 + 税后去向 已近 12。
   */
  MaxPayrollItems: 15,
  /**
   * **拆分**（同一账户的多腿）的**腿数上限** —— C 域 PRD §5.2.2（与工资条的 15 刻意区分）。
   *
   * ⚠️ `DR-035` v1.1 改判后「拆分 = 一笔交易的**多条腿**」（不再是"容器 + 子项交易"）
   * ⇒ 本条按**腿数**计，且只在"全部腿在同一账户"时生效（跨账户落 `MaxPayrollItems` 档）。
   */
  MaxSplitChildren: 10,
  /**
   * 复合交易的**腿数**防御上限（⚠️ **非产品口径**）。
   * 工资条 15 子项里若有转账子项（各 2 腿）⇒ 真实上界约 30；取 40 留余量。
   */
  MaxLegsPerCompositeTxn: 40,
  /** 搜索框关键词长度上限（⚠️ **非产品口径**：规格未规定；量级参照备注 200 取更小值） */
  KeywordMaxLength: 50,
  /**
   * 单次导出的行数上限（⚠️ **防御性**，非产品口径）。
   * 导出不分页（分页会让用户拿到半个文件而不自知）⇒ 需要一个兜底上限防"一键导出把内存打爆"。
   * 超限**报 400 并提示缩小筛选范围**，**不静默截断**（与分页上限的处置一致）。
   */
  MaxExportRows: 50_000,
} as const;

// ───────────────────── 10. 账户（B 域 · 规格 §1 / §8 · B 域 PRD §5.1 / §5.2） ─────────────────────

/**
 * 账户与信用卡策略常量。
 *
 * ## 🔴 `BillingDayMax` 的取值：**31**（`2026-10-12` 由 28 放宽，依据真实数据）
 *
 * **放宽理由**：真实用户（`v1` 生产 `led_5be2qqewj4g5wxl3z72u`）存在 **5 张卡账单日 = 31**
 * —— 蚂蚁花呗 / 京东白条 / 美团月付 / 抖音月付（消费信贷产品按**月末**出账）+ 另有 `1` / `27` 等值。
 * 锁死在 28 会让这批真实账户**根本无法录入**（`AC-B2.2` 的失败方式从"窗口变形"变成"收不了数据"，
 * 前者可订正、后者是硬拒绝）。
 *
 * **29–31 为什么是安全的**（原先的顾虑已被现成机制化解）：
 * - `dayInMonth()` 用 `clampInt(dom, 1, lastDayOfMonth(y, m))` ⇒ `31` 在 2 月**自动收成 28/29**、
 *   在 4/6/9/11 月收成 30，与银行「**月末出账**」语义**一致**（这正是花呗/白条的真实行为）。
 * - `addMonths()` 同样按目标月天数 clamp ⇒ `windowStart = addMonths(billDay, -1)` 不会溢出到下月。
 * - ⇒ 窗口 `[windowStart, windowEnd)` **逐月连续、不自交**，2 月不再有空洞。
 *
 * ⚠️ **口径边界（需拍板）**：`31` 在短月表现为"月末"，因此**同一个月内 `windowStart == windowEnd`
 * 的可能性不存在**，但**不同月份的第 31 天不是同一个"日"**（3/31 vs 2/28）。
 * 这与银行一致，但与"每月固定某日"的直觉不同 ⇒ 若产品侧要求"必须字面同等日"，
 * 应改回 28 并把这类卡**在产品层引导改为 1 号**，而不是在此处放宽。
 */
export const AccountPolicy = {
  /** 账单单日 / 还款日下界（日） */
  BillingDayMin: 1,
  /** 账单日 / 还款日上界（日）= **31** —— 见上方说明（`2026-10-12` 由 28 放宽以承接真实数据） */
  BillingDayMax: 31,
  /** 信用卡使用率「接近上限」阈值 —— `E4` 标度，`8000` = 80%（`--c-notice` 琥珀） */
  UsageNoticeE4: 8000,
  /** 信用卡使用率「已超」阈值 —— `E4` 标度，`10000` = 100%（`--c-alert` 朱红） */
  UsageOverE4: 10000,
  /** 「临近还款日」天数（含当天）—— 距还款日 ≤ 此值即升为琥珀提示（B 域 PRD §5.2.3） */
  DueSoonDays: 3,
  /**
   * 账户名长度上限（字符）。
   * ⚠️ **非产品口径** —— 规格与 B 域 PRD 均未规定；量级参照账本名（30）。
   */
  NameMaxLength: 30,
  /**
   * 「对手方」长度上限（字符）—— `DR-046` D4。
   *
   * ⚠️ **非产品口径** —— 规格与 Q 域 PRD 均未规定；量级参照 `NameMaxLength`（30）。
   * 存在的理由只是拦住无界文本。将来规格给出正式值 ⇒ 以正式值为准并改此处。
   */
  CounterpartyMaxLength: 30,
  /**
   * 单账本账户数**防御性**上限。
   * ⚠️ **非产品口径**：规格未设上限。存在的理由只是拦住"无限建账户"这一类廉价风险；
   * 若将来规格给出正式值，**以正式值为准并改此处**。
   */
  MaxAccountsPerLedger: 200,
  /** 初始余额绝对值上限（分）= 1 亿元 —— 防误输入多打几个 0 把净资产表打穿 */
  InitialBalanceAbsLimitCents: 10_000_000_000,
  /**
   * 贷款年利率上限 —— `E4` 标度，`1_000_000` = **100%**（`DR-044` D2/D3）。
   *
   * ⚠️ **非产品口径**（规格与 Q 域 PRD 未给上限）：存在的理由只是拦住
   * "误把万分之一当百分数填"（如把 `3.36%` 填成 `33600` = 336%）。
   * 上限取 100% 是因为超过它的"年利率"在家庭场景下必是录入错误。
   */
  AnnualRateE4Max: 1_000_000,
  /**
   * 贷款期数上限（期）—— `1_200` = **100 年 × 12 期**（`DR-044` D2）。
   *
   * ⚠️ **非产品口径**：理由同 `AnnualRateE4Max`，只拦"多打几个 0"。
   * 🔴 本值是**总期数**（`DR-044` §5 第 3 项拍板），剩余期数由 `loan_schedules` 数出。
   */
  TermsMax: 1_200,
} as const;

// ───────────────────── 11. 保单台账（B 域 · B5 · B 域 PRD §5.5.5） ─────────────────────

/**
 * 保单台账策略常量（B 域 PRD **§5.5.5**）。
 *
 * ⚠️ **除 `MaxInsureds` 外全部是「非产品口径」的防御性上限** —— 规格与 B 域 PRD
 * **均未规定**这些长度与数值。存在理由只是拦住"廉价风险"（超长文本、误把「元」当「分」）。
 * 若产品侧给出正式值，**以正式值为准并改此处**（唯一来源）。
 */
export const InsurancePolicy = {
  /**
   * 单张保单的**被保人上限**。
   * ⚠️ **非产品口径**：B 域 PRD §5.5.5 只说"P0 即建 1:N、UI 首版只露 1 个"，未给上限。
   * 取 **10** 是按家庭场景量级（家庭单 / 团体险）留的余量。
   */
  MaxInsureds: 10,
  /** 保单号长度上限（字符） */
  PolicyNoMaxLength: 64,
  /** 保险公司名长度上限（字符） */
  CompanyMaxLength: 60,
  /** 险种名长度上限（字符） */
  InsuranceTypeMaxLength: 40,
  /** 缴费方式长度上限（字符） */
  PaymentModeMaxLength: 20,
  /**
   * 当事人（投保人 / 被保人 / 受益人）长度上限（字符）。
   * 兼容"存系统用户 id"（`usr_` + ≤32）与"存姓名"两种形态。
   */
  PartyMaxLength: 60,
  /**
   * 金额类字段（保额 / 现金价值 / 年缴保费）的**防御性**上限（分）= 100 亿元。
   * 理由同 `InitialBalanceAbsLimitCents`：把「元」当「分」填会放大 100 倍，
   * 上限的作用是让这种笔误**当场被拦**而不是把净资产表打穿。
   */
  MaxPolicyAmount: 100_000_000_000,
} as const;

/**
 * **N 域恢复（N4 §5.4.2）的防御性数值** —— 唯一来源。
 *
 * ⚠️ **非产品口径**：PRD 未规定这些数值；存在理由是拦住"廉价风险"：
 * ① 备份 JSON 可达数 MB，而 Express 默认请求体上限 **100 KB** ⇒ 不显式放开则**恢复必然 413**
 *    （本批实测发现，见 changelog）；② 预览/结果里的清单若不加界，一份大备份能把响应撑成第二份备份。
 */
export const RestorePolicy = {
  /**
   * 恢复接口的**请求体上限**（字节）= 32 MiB。
   *
   * 🔴 只给**恢复两条路由**放宽，其余路由保持 {@link RestorePolicy.DefaultBodyBytes} ——
   * 全站放开会把"未鉴权路由（如登录）也能吃 32 MB"这个面一起放开。
   */
  MaxBodyBytes: 32 * 1024 * 1024,
  /** 其余路由的请求体上限（= Express 默认 100 KB，本批**显式化**为常量，行为不变） */
  DefaultBodyBytes: 100 * 1024,
  /** 预览里列出的**冲突明细**上限（超出 ⇒ 截断 + `conflictsTruncated` 上报） */
  MaxConflictItems: 50,
  /** 执行结果里列出的**丢弃清单**上限（超出 ⇒ 截断 + `discardsTruncated` 上报） */
  MaxDiscardItems: 100,
} as const;

// ───────────────────────── 8.9 储蓄目标（I 域 · `DR-050`） ─────────────────────────

/**
 * 储蓄目标策略（I 域 PRD §5.1 / §5.2 / §5.3 · `AC-I1.x` / `AC-I2.x` / `AC-I3.x`）。
 *
 * 🔴 **本批（`85`）首次把 I 域的数落为代码常量**。此前 `savings_goals` 表已在（12 列），
 * 但「里程碑档位」「名称长度」这类数只存在于 PRD 表格与原型文案里。
 *
 * ⚠️ **自动攒相关的常量刻意不存在**：它依赖 F3 发薪日工作流（未实现），
 * 现在写一个「每期默认攒多少」只会让人以为该功能已在（`DR-050` D8）。
 */
export const GoalPolicy = {
  /**
   * 里程碑档位（百分数）—— **每 25% 一次**（`§5.3.1` · `AC-I3.1`）。
   *
   * 🔴 **只升不降**（`AC-I3.2`）：档位落库为**历史最高**，取出导致回落时**不覆盖**。
   */
  MilestoneTiers: [25, 50, 75, 100] as const,
  /**
   * 目标名称长度上限（字符）。
   * ⚠️ **非产品口径** —— PRD 未规定；量级参照 `AccountPolicy.NameMaxLength`（30）。
   */
  NameMaxLength: 30,
  /**
   * 攒/取备注长度上限（字符）。⚠️ **非产品口径**，与流水备注同量级（200）。
   */
  NoteMaxLength: 200,
  /**
   * 单账本目标数**防御性**上限。
   * ⚠️ **非产品口径**：PRD 只要求「**≥5 个并存**」（`AC-I1.3`，是**下限**不是上限）。
   * 这里给一个远高于下限的值，只为拦住"无限建目标"这一类廉价风险。
   */
  MaxGoalsPerLedger: 100,
  /**
   * 节奏判定所需的**最少观察月数**。不足 ⇒ `pace = 'tooEarly'`（**不猜**）。
   *
   * 理由：只有 1 天的数据算不出"月均速率"，硬算会把「昨天刚攒了 ¥10,000」
   * 外推成「每月 ¥10,000」，从而给用户一个**过度乐观**的预计达成日。
   */
  MinMonthsForPace: 1,
} as const;

// ───────────────────────── 8.10 账单导入（D 域 · `DR-051`） ─────────────────────────

/**
 * 账单导入策略（D 域 PRD **§5.1.2** / **§5.2** / **§5.3.1** / **§5.2.2**）。
 *
 * 🔴 **本批（`86`）首次把 D 域的数落为代码常量**。此前「10 MB / 10,000 行 / 30 天可撤销」
 * 这些值只存在于 PRD 表格里。
 *
 * ⚠️ **不写「规则覆盖率」之类的指标常量**：那是验收度量，不是运行时策略。
 */
export const ImportPolicy = {
  /** 单文件大小上限（字节）= **10 MB**（`§5.1.2` · `AC-D1.11`） */
  MaxBytes: 10 * 1024 * 1024,
  /** 单文件行数上限（`§5.1.2` · `AC-D1.11`） */
  MaxRows: 10000,
  /** 单次导入文件数上限（`§5.1.2`）—— ⚠️ 本批**一次只做 1 个文件**，此值供将来批量用 */
  MaxFiles: 5,
  /**
   * 乱码阈值：替换字符**千分比**上限（5 = 0.5%）。
   *
   * 🔴 用千分比而不是百分比：判据写成 `count * 1000 > 5 * len` 是**整数比较**，
   * 规避端侧禁除法（`AC-P1.2`）。
   */
  MaxReplacementPermille: 5,
  /**
   * 表头扫描行数上限 —— 微信/支付宝账单前面都有一大段说明文字。
   * ⚠️ **非产品口径**：PRD 未规定；取 40 是因为两家实测都在 20 行以内。
   */
  HeaderScanRows: 40,
  /** L3 疑似重复的商户相似度阈值（百分数，85 = 0.85 · `§5.2`） */
  SimilarityPercent: 85,
  /** 历史确认规则：最少笔数（`§5.3` 的 `count ≥ 3`） */
  HistoryMinHits: 3,
  /** 历史确认规则：最低占比（E4，8000 = 0.80 · `§5.3`） */
  HistoryMinRatioE4: 8000,
  /** 历史确认规则的观察窗口（天）= **近 90 天**（`§5.3`） */
  HistoryWindowDays: 90,
  /** 字典**唯一命中**的置信度（E4，8000 = 0.80 · `§5.3`） */
  DictSingleHitE4: 8000,
  /** 字典**最长命中**（多个候选）的置信度（E4，9000 = 0.90 · `§5.3`） */
  DictLongestHitE4: 9000,
  /** 置信度「高」档下界（E4，8500 = 0.85 · `§5.3.1`） */
  HighBandE4: 8500,
  /** 置信度「中」档下界（E4，5000 = 0.50 · `§5.3.1`） */
  MidBandE4: 5000,
  /** 批次可撤销窗口（天）= **30 天**（`§5.2.2` · `AC-D2.9`） */
  RevocableDays: 30,
  /**
   * 记账习惯提醒的「未消化」重推间隔（天）—— H 域 ⑤（`AC-H1.6`：7 天内每 3 天）。
   * 🔴 放在这里而不是 `ReminderPolicy`：它描述的是**D 域的"未消化"状态**，
   * H 域只是按时去问（同「G 算 H 拉」的分工）。
   */
  UnsettledRepeatDays: 3,
  /** 记账习惯提醒的观察窗口（天）—— 导入后 7 天内才催（`AC-H1.6`） */
  UnsettledWindowDays: 7,
} as const;

// ───────────────────── 8.11 发薪日工作流（F 域 F3 · `DR-052`） ─────────────────────

/**
 * 发薪日工作流策略（`F域PRD` §4.7 / §7.3 · `DR-052`）。
 *
 * 🔴 **本批（`87`）首次把 F3 的数落为代码常量**。此前「90 天候选窗口」「固定攒最小 1 元」
 * 只写在 PRD 与 DR 里。
 */
export const PaydayPolicy = {
  /**
   * 候选窗口（天）—— 近 90 天内的"未分配工资"才在入口列表里。
   *
   * ⚠️ **非产品口径**：PRD 未规定窗口；取 90 与「历史确认规则」的观察窗口
   * （`ImportPolicy.HistoryWindowDays`）同量级，理由是"三个月前的工资再分配已无意义"。
   */
  CandidateLookbackDays: 90,
  /**
   * 🔴 **工资分类白名单**（`DR-052` D1）—— 只有这两个分类的收入会触发 F3。
   *
   * | id | 名称 |
   * |---|---|
   * | `cat-salary` | 工资（L1） |
   * | `cat-salary-wage` | 工资（L2） |
   *
   * 🔴 **刻意不含** `cat-salary-bonus`（年终奖）/ `cat-salary-allowance`（奖金津贴）/
   * `cat-salary-subsidy`（补助）：F3 的语义是**月度发薪**的分配节奏，年终奖不规律。
   * 放开只需在此加一项（属"延后不返工"）。
   */
  SalaryCategoryIds: ['cat-salary', 'cat-salary-wage'] as const,
  /** 固定额自动攒的下限（分）= **1 元** —— 低于它攒一次没有意义，且会让进度被噪声推动 */
  MinFixedSaveCents: 100,
  /** 比例自动攒的上限（E4）= **100%** */
  PercentMaxE4: 10000,
  /**
   * 一次分配最多涉及的目数。
   * ⚠️ **非产品口径**（PRD 未规定）；取 `GoalPolicy.MaxGoalsPerLedger` 的量级下界，
   * 只为拦住"一次分配 100 个目标"的异常请求。
   */
  MaxAllocations: 50,
} as const;
